import React from 'react';
import { useNavigate } from 'react-router-dom';
import { StarIcon, MapPinIcon, PlaneIcon, TagIcon } from 'lucide-react';
import Button from '../common/Button';
import { Package } from '../../utils/types';
interface PackageCardProps {
  package: Package;
}
const PackageCard: React.FC<PackageCardProps> = ({
  package: pkg
}) => {
  const navigate = useNavigate();
  const handleSelect = () => {
    // For recommended packages shown after flight selection, keep the existing behavior
    if (window.location.pathname.includes('/booking/select-hotel')) {
      navigate(`/booking/select?packageId=${pkg.id}`);
    } else {
      // For regular package search results, direct to the new flow
      navigate(`/booking/select?type=package&flightId=${pkg.flight.id}`);
    }
  };
  // Format the departure and arrival times
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  // Generate star rating display
  const renderStars = (count: number) => {
    return Array(5).fill(0).map((_, i) => <StarIcon key={i} className={`h-4 w-4 ${i < count ? 'text-yellow-400' : 'text-gray-300'}`} fill={i < count ? 'currentColor' : 'none'} data-id="element-426" />);
  };
  return <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow" data-id="element-427">
      <div className="relative" data-id="element-428">
        <div className="absolute top-0 left-0 bg-blue-600 text-white py-1 px-3 rounded-br-lg" data-id="element-429">
          <div className="flex items-center" data-id="element-430">
            <TagIcon className="h-4 w-4 mr-1" data-id="element-431" />
            <span className="text-sm font-medium" data-id="element-432">Save ${pkg.savings}</span>
          </div>
        </div>
        <img src={pkg.hotel.thumbnail} alt={pkg.hotel.name} className="w-full h-48 object-cover" data-id="element-433" />
      </div>
      <div className="p-6" data-id="element-434">
        <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-435">
          {pkg.hotel.name}
        </h3>
        <div className="flex items-center mt-1 mb-3" data-id="element-436">
          <div className="flex mr-2" data-id="element-437">{renderStars(pkg.hotel.stars)}</div>
          <span className="text-sm text-gray-500" data-id="element-438">
            {pkg.hotel.stars}-star hotel
          </span>
        </div>
        <div className="flex items-center text-sm text-gray-600 mb-4" data-id="element-439">
          <MapPinIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-440" />
          <span data-id="element-441">
            {pkg.hotel.city}, {pkg.hotel.country}
          </span>
        </div>
        <div className="border-t border-b border-gray-200 py-4 my-4" data-id="element-442">
          <div className="flex items-start" data-id="element-443">
            <PlaneIcon className="h-5 w-5 text-blue-600 mr-2 mt-0.5" data-id="element-444" />
            <div data-id="element-445">
              <h4 className="font-medium text-gray-900" data-id="element-446">Flight Details</h4>
              <p className="text-sm text-gray-600 mt-1" data-id="element-447">
                {pkg.flight.airline} • Flight {pkg.flight.flightNumber}
              </p>
              <div className="flex items-center text-sm mt-2" data-id="element-448">
                <div className="mr-3" data-id="element-449">
                  <p className="font-medium" data-id="element-450">
                    {formatTime(pkg.flight.departureTime)}
                  </p>
                  <p className="text-gray-500" data-id="element-451">{pkg.flight.departureAirport}</p>
                </div>
                <div className="border-t border-gray-300 w-8 mx-2" data-id="element-452"></div>
                <div data-id="element-453">
                  <p className="font-medium" data-id="element-454">
                    {formatTime(pkg.flight.arrivalTime)}
                  </p>
                  <p className="text-gray-500" data-id="element-455">{pkg.flight.arrivalAirport}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col md:flex-row justify-between items-center" data-id="element-456">
          <div className="mb-4 md:mb-0" data-id="element-457">
            <div className="flex items-baseline" data-id="element-458">
              <span className="text-2xl font-bold text-blue-600" data-id="element-459">
                ${pkg.totalPrice}
              </span>
              <span className="text-gray-500 text-sm ml-1" data-id="element-460">total</span>
            </div>
            <span className="text-xs text-gray-500" data-id="element-461">Flight + 7 nights</span>
          </div>
          <Button variant="primary" onClick={handleSelect} data-id="element-462">
            Select Package
          </Button>
        </div>
      </div>
    </div>;
};
export default PackageCard;